import { createFileRoute, Link } from "@tanstack/react-router";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import buddhaKaOfficial from "@/assets/buddha-ka-official.png.asset.json";
import { preloadImageLink } from "@/lib/images";
import { BOOKS, RELEASES, STREAMING_LINKS, getCurrentRelease, getNextRelease, formatReleaseDate } from "@/lib/content";
import { getNews } from "@/lib/news";
import { SITE_URL } from "@/lib/site";
import {
  PORTRAIT_ALT,
  PORTRAIT_URL,
  breadcrumbs,
  ldScript,
  orgRef,
  pageNode,
  personRef,
  seoMeta,
} from "@/lib/schema";


const LATEST_NEWS = getNews()[0];
// Next scheduled release, derived from canonical release dates. When nothing
// further is announced, the most recent live release is surfaced as out now.
const NEXT_RELEASE = getNextRelease(RELEASES);
const CURRENT_RELEASE = getCurrentRelease(RELEASES);



const FAQ: Array<{ q: string; a: string }> = [
  {
    q: "Who is Buddha KA?",
    a: "Buddha KA is an independent Chicago-born recording artist, author, producer, and creator of KAfrequency — a first-party ecosystem uniting music, books, daily spoken activations, visual worlds, and consciousness systems.",
  },
  {
    q: "What is Buddha KA's real or author name?",
    a: "His legal name and author byline is Curtis Shores. Buddha KA is his professional artist name.",
  },
  {
    q: "Where is Buddha KA from?",
    a: "Chicago, Illinois. He lived in Chicago until age 13, moved to Wisconsin at 13, and moved to Las Vegas at 16. He has since lived and worked between Chicago, Texas, and Las Vegas.",
  },
  {
    q: "What kind of music does Buddha KA make?",
    a: "Buddha KA works within his own framework called KA Flow — an integration of hip-hop, soul/R&B, spoken wisdom, and spiritual science, written as inner technology rather than entertainment.",
  },
  {
    q: "What is KAfrequency?",
    a: "KAfrequency is the artist-owned creative ecosystem Buddha KA founded. It joins music, written systems, daily activations, cinematic visuals, and consciousness technologies under one canon.",
  },
  {
    q: "What is KA Flow?",
    a: "KA Flow is Buddha KA's musical framework — hip-hop, soul, spoken wisdom, and spiritual science engineered as a single instrument. It governs the KAfrequency catalog.",
  },
  {
    q: "Where can people listen to or read Buddha KA?",
    a: "On buddhaka.com, and on the streaming and social platforms officially connected to Buddha KA — including Spotify, Apple Music, YouTube, SoundCloud, Instagram, TikTok, and Facebook.",
  },
  {
    q: "How can media contact Buddha KA?",
    a: "Media inquiries and interview requests go through the official contact form at buddhaka.com/contact. Full press materials are available at buddhaka.com/press.",
  },
];

export const Route = createFileRoute("/about")({
  component: About,
  head: () => {
    const url = `${SITE_URL}/about`;
    const title = "Buddha KA Biography | Artist, Author & KAfrequency Founder";
    const description =
      "Buddha KA (Curtis Shores) is a Chicago-born independent recording artist, author, producer, and creator of KAfrequency: music, books, and daily activations.";
    return {
      ...seoMeta({
        title,
        description,
        path: "/about",
        image: PORTRAIT_URL,
        imageAlt: PORTRAIT_ALT,
        ogType: "profile",
      }),
      // This array replaces seoMeta()'s links, so the production canonical is
      // re-declared here alongside the LCP preload for the portrait.
      links: [
        { rel: "canonical", href: url },
        preloadImageLink(buddhaKaOfficial.url, "(max-width: 768px) 92vw, 460px"),
      ].filter(Boolean) as Array<Record<string, string>>,
      scripts: [
        // Canonical ProfilePage for the Buddha KA Person node. The Person
        // itself is defined once sitewide (src/lib/schema.ts) — referenced
        // here by @id so no conflicting duplicate entity is emitted.
        ldScript(
          pageNode({
            type: "ProfilePage",
            id: `${url}#profilepage`,
            url,
            name: "About Buddha KA",
            description,
            image: PORTRAIT_URL,
            imageCaption: PORTRAIT_ALT,
            mainEntity: personRef(),
            about: [personRef(), orgRef()],
            extra: { significantLink: [`${SITE_URL}/music`, `${SITE_URL}/press`, `${SITE_URL}/news`, `${SITE_URL}/contact`] },
          }),
        ),
        ldScript({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${url}#faq`,
          isPartOf: { "@id": `${url}#profilepage` },
          about: personRef(),
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
        ldScript(breadcrumbs([["About Buddha KA", "/about"]])),
      ],
    };
  },
});


function Section({ eyebrow, title, children }: { eyebrow: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{eyebrow}</p>
      <h2 className="mt-2 font-serif text-3xl leading-tight sm:text-4xl">{title}</h2>
      <div className="mt-5 space-y-5 text-lg leading-relaxed text-ivory/85">{children}</div>
    </section>
  );
}

function About() {
  const sameAs = Object.entries(STREAMING_LINKS).filter(([, v]) => Boolean(v)) as Array<[string, string]>;
  return (
    <>
      <section className="relative border-b hairline">
        <div className="absolute inset-0 -z-10 halo opacity-40" aria-hidden />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-gold">About</p>
              <h1 className="mt-4 font-serif text-5xl leading-tight sm:text-6xl md:text-7xl">
                Buddha KA
              </h1>
              <p className="mt-4 text-sm uppercase tracking-[0.25em] text-muted-foreground">
                Curtis Shores · Artist · Author · Producer · Creator of KAfrequency
              </p>
              <p className="mt-8 max-w-xl font-serif text-xl italic text-ivory/90">
                Chicago-born independent recording artist, author, producer, visual world-builder, and creator of KAfrequency.
              </p>
              <p className="mt-6 max-w-xl text-muted-foreground">
                A body of work uniting music, books, daily spoken activations, cinematic visual worlds, and
                consciousness systems — under one canon.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/music" className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">
                  Listen
                </Link>
                <Link to="/books" className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian">
                  Read
                </Link>
                <Link to="/activations" className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian">
                  Daily activations
                </Link>
                <Link to="/press" className="rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold hover:text-gold">
                  Press kit
                </Link>
                <Link to="/contact" className="rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold hover:text-gold">
                  Contact
                </Link>
                <Link to="/news" className="rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold hover:text-gold">
                  Newsroom
                </Link>
              </div>
            </div>
            <div className="rounded-2xl border border-gold/40 bg-charcoal p-3 shadow-halo">
              <ResponsiveImage
                src={buddhaKaOfficial.url}
                alt="Buddha KA — official portrait"
                priority
                sizes="(max-width: 768px) 92vw, 460px"
                className="aspect-[4/5] w-full rounded-xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <Section eyebrow="Who" title="Who is Buddha KA.">
        <p>
          Buddha KA — legal name <span className="text-ivory">Curtis Shores</span> — is a Chicago-born
          independent recording artist, author, producer, visual world-builder, and creator of{" "}
          <span className="text-ivory">KAfrequency</span>. He operates the entire ecosystem himself, outside
          the traditional label system, treating music as inner technology rather than entertainment.
        </p>
      </Section>

      <Section eyebrow="Origin" title="From Chicago to Wisconsin to Las Vegas.">
        <p>
          Born and raised in Chicago until age 13. Moved to Wisconsin at 13. Moved to Las Vegas at 16. He has
          since lived and worked between Chicago, Texas, and Las Vegas — and continues to move between them.
        </p>
        <p>
          Chicago's rhythmic literacy, the interior stillness of the Wisconsin years, and the desert clarity of
          the Southwest each pressed into how his music sounds and how his writing thinks.
        </p>
      </Section>

      <Section eyebrow="Music" title="KA Flow.">
        <p>
          Buddha KA works inside his own musical framework called <span className="text-ivory">KA Flow</span> —
          an integration of hip-hop, soul and R&B, spoken wisdom, and spiritual science engineered as a single
          instrument. KA Flow is not a subgenre. It is the lane he built to hold philosophy without softening
          it: bars used as instruction, cadence used as breath work, arrangement used as architecture.
        </p>
        <p>
          KA Flow governs the KAfrequency catalog, including the sutra cycle, Golden Frequency, The Golden
          Wave, and Ascended and Armed.
          {NEXT_RELEASE ? (
            <>
              {" "}The next KAfrequency transmission,{" "}
              <Link to="/music/$slug" params={{ slug: NEXT_RELEASE.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">{NEXT_RELEASE.title}</Link>, arrives{" "}
              <time dateTime={NEXT_RELEASE.releaseDate}>{formatReleaseDate(NEXT_RELEASE)}</time>.
            </>
          ) : CURRENT_RELEASE ? (
            <>
              {" "}The latest KAfrequency transmission,{" "}
              <Link to="/music/$slug" params={{ slug: CURRENT_RELEASE.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous">{CURRENT_RELEASE.title}</Link>, is out now
              {CURRENT_RELEASE.releaseDate ? (
                <>
                  {" — released "}
                  <time dateTime={CURRENT_RELEASE.releaseDate}>{formatReleaseDate(CURRENT_RELEASE)}</time>
                </>
              ) : null}
              .
            </>
          ) : null}
        </p>
      </Section>

      <Section eyebrow="Ecosystem" title="KAfrequency.">
        <p>
          KAfrequency is the artist-owned ecosystem Buddha KA founded. Its arms are designed to reinforce each
          other rather than compete for attention:
        </p>
        <ul className="ml-5 list-disc space-y-2 text-ivory/90">
          <li><Link to="/artist/buddha-ka" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Official Buddha KA artist page</Link> — verified streaming profiles and the full discography.</li>
          <li><Link to="/music" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Music</Link> — the sutra cycle and the KA Flow catalog.</li>
          <li><Link to="/books" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Books</Link> — 47 Gates of Consciousness, The Crystallized Mind, and companion working manuals.</li>
          <li><Link to="/activations" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Daily activations</Link> — short spoken transmissions written as inner technology for the ordinary day.</li>
          <li><Link to="/gates" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">Consciousness systems</Link> — the 47 Gates journey, the Life Journey Map, and the interactive practice surface.</li>
          <li><Link to="/academy" className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">The Academy</Link> — long-form study paths and guided arcs.</li>
        </ul>
        {LATEST_NEWS && (
          <p className="mt-6 text-sm text-muted-foreground">
            Latest official update:{" "}
            <Link to="/news/$slug" params={{ slug: LATEST_NEWS.slug }} className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold">
              {LATEST_NEWS.title}
            </Link>
          </p>
        )}

      </Section>

      <Section eyebrow="Books" title="Written systems, not commentary.">
        <p>
          The written work — including 47 Gates of Consciousness and The Crystallized Mind — is built as
          practice: passages to be revisited, worked, and returned to. The books share one canonical source
          with the music and the activations, so a reader and a listener are moving through the same field.
        </p>
      </Section>

      <Section eyebrow="Daily Activations" title="Spoken instruction for ordinary days.">
        <p>
          The daily activations translate the KAfrequency field into a short, spoken practice — an intention,
          a breath instruction, a phrase, and a journal invitation. They are not affirmations or motivational
          content. They are structured practice, one transmission at a time.
        </p>
      </Section>

      <Section eyebrow="Philosophy" title="Music as inner technology.">
        <p>
          Inner sovereignty is not a mood. It is an architecture. Practical spiritual systems — for the breath,
          the field, the ledger of attention, the ordering of the day — are what transform philosophy into a
          life. Music, in this work, is not decoration around that philosophy. It is the room the philosophy
          is practiced in.
        </p>
        <p>
          The records are layered to be replayed for years, not consumed in a scroll. They are written for
          cultural longevity, not the algorithm&rsquo;s short memory.
        </p>
      </Section>

      <Section eyebrow="Selected Works" title="A body of work in motion.">
        <ul className="space-y-3 text-base">
          {RELEASES.map((r) => (
            <li key={r.slug} className="flex flex-wrap items-baseline gap-x-3 border-b hairline pb-3">
              <Link to="/music/$slug" params={{ slug: r.slug }} className="font-serif text-lg text-gold hover:text-gold-luminous">
                {r.title}
              </Link>
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {r.type}
                {r.year ? ` · ${r.year}` : ""}
                {r.status === "upcoming"
                  ? ` · Arrives ${formatReleaseDate(r) ?? "soon"}`
                  : r.status === "in-progress"
                    ? " · In progress"
                    : ""}
              </span>
            </li>
          ))}
          {BOOKS.map((b) => (
            <li key={b.slug} className="flex flex-wrap items-baseline gap-x-3 border-b hairline pb-3">
              <Link to="/books/$slug" params={{ slug: b.slug }} className="font-serif text-lg text-gold hover:text-gold-luminous">
                {b.title}
              </Link>
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Book · {b.category}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section eyebrow="Official destinations" title="Where to find Buddha KA.">
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {sameAs.map(([k, url]) => (
            <li key={k}>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="flex min-h-11 items-center justify-center rounded-full border border-gold px-3 py-2 text-xs text-gold hover:bg-gold hover:text-obsidian"
              >
                {k} ↗
              </a>
            </li>
          ))}
        </ul>
      </Section>

      <section className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">FAQ</p>
        <h2 className="mt-2 font-serif text-3xl leading-tight sm:text-4xl">Frequently asked.</h2>
        <div className="mt-6 divide-y divide-white/5 rounded-2xl border hairline bg-charcoal">
          {FAQ.map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-ivory">
                <span className="font-serif text-lg">{f.q}</span>
                <span className="text-gold transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-24 sm:px-6">
        <div className="rounded-2xl border hairline bg-obsidian/60 p-6 sm:p-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Press · Contact</p>
          <h2 className="mt-2 font-serif text-2xl text-ivory">Working with Buddha KA.</h2>
          <p className="mt-3 text-muted-foreground">
            Bios, official portrait, fact sheet, and story angles are available in the press kit. Interview and
            media requests go through the official contact form.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/press" className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian">
              Media kit →
            </Link>
            <Link to="/contact" className="rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold hover:text-gold">
              Press contact
            </Link>
            <Link to="/news" className="rounded-full border hairline px-5 py-2.5 text-sm hover:border-gold hover:text-gold">
              Official newsroom
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
